import { useEffect, useState } from 'react';
import { AppointmentCard } from './components/appointment-card/appointment-card';
import { getAllAppointsmentsWithCustomer } from '../../core/services/appointment.service';
import { Appointment } from '../../core/models/appointment';
import './appointment-page.scss';
import { Loading } from '../../components/loading/loading';
import { Empty, notification, DatePicker, Pagination } from 'antd';
import { AppointmentStatus } from '../../core/models/appointment-status.enum';
import { useTranslation } from 'react-i18next';
import { NotificationType } from '../../core/types/notification.type';
import { AppointmentHeader } from './components/appointment-header/appointment-header';
import dayjs from 'dayjs';
import { DiscardModal } from '../../components/discard-modal/discard-modal';

export const AppointmentPage = () => {
	const { t } = useTranslation();
	const [api, contextHolder] = notification.useNotification();

	const [appointments, setAppointments] = useState<Appointment[]>([]);
	const [selectedAppointmentId, setSelectedAppointmentId] = useState<number>();
	const [totalAppointments, setTotalAppointments] = useState<number>(0);
	const [pageOffset, setPageOffset] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(true);
	const [openDiscardModal, setOpenDiscardModal] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<Date>(dayjs().toDate());
	const [endDate, setEndDate] = useState<Date>(dayjs().add(window.bookrPluginData.settings.bookingDaysAhead, 'day').toDate());

	const pageSize = 20;
	const { RangePicker } = DatePicker;
	const dateFormat = 'DD.MM.YYYY';

	useEffect(() => {
		loadAppointments();
	}, [startDate, endDate]);

	async function loadAppointments() {
		setLoading(true);
		const appointments = await getAllAppointsmentsWithCustomer(startDate, endDate);
		setAppointments(appointments);
		setTotalAppointments(appointments.length);
		setLoading(false);
	}

	function onChangedAppointmentStatus(updatedAppointment: Appointment): void {
		const message =
			updatedAppointment.status === AppointmentStatus.ACCEPTED
				? `${t('backEnd.appointment.appointmentAccepted')}`
				: `${t('backEnd.appointment.appointmentRejected')}`;
		const notificationType = updatedAppointment.status === AppointmentStatus.ACCEPTED ? 'success' : 'error';

		api[notificationType as NotificationType]({
			message: `${message}`,
		});

		const updatedAppointments = appointments.map((appointment) => {
			return appointment.id === updatedAppointment.id ? updatedAppointment : appointment;
		});

		setAppointments([...updatedAppointments]);
	}

	function handleChangeDateRange(dates: any): void {
		if (!dates[0] || !dates[1]) return;

		const startDate = dayjs(dates[0]).toDate();
		const endDate = dayjs(dates[1]).toDate();

		setStartDate(startDate);
		setEndDate(endDate);
	}

	function onChangePagination(page: number): void {
		const offset = page === 1 ? 0 : page * 10;
		setPageOffset(offset);
	}

	function onClickDelete(appointmentId: number): void {
		setOpenDiscardModal(true);
		setSelectedAppointmentId(appointmentId);
	}

	function onHandleDelete(accept: boolean): void {
		if (accept) {
			const index = appointments.findIndex((appointment) => appointment.id === selectedAppointmentId);

			if (index > -1) {
				appointments.splice(index, 1);
				setAppointments([...appointments]);

				api['error']({
					message: t('backEnd.appointment.appointmentDeleted'),
				});
			}
		}

		setOpenDiscardModal(false);
	}

	return (
		<>
			{contextHolder}
			<DiscardModal open={openDiscardModal} onClose={onHandleDelete} />
			<AppointmentHeader appointments={appointments} />
			<div className='action-bar'>
				<RangePicker defaultValue={[dayjs(startDate), dayjs(endDate)]} format={dateFormat} onChange={handleChangeDateRange} />
			</div>
			<Loading isLoading={loading}>
				<div className='appointment-card-container'>
					{appointments.length === 0 ? (
						<Empty description={t('common.noDataAvailable')} />
					) : (
						appointments.slice(pageOffset, pageOffset + pageSize).map((appointment) => {
							return (
								<AppointmentCard
									key={appointment.id}
									appointment={appointment}
									onChanged={onChangedAppointmentStatus}
									onDelete={(appointmentId) => onClickDelete(appointmentId)}
								/>
							);
						})
					)}
					<Pagination align='center' defaultCurrent={1} pageSize={pageSize} total={totalAppointments} onChange={onChangePagination} />
				</div>
			</Loading>
		</>
	);
};
